import { Button, Grid } from '@mui/material'
import { DependencyList, useContext, useEffect, useRef, useState } from 'react'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import {} from 'react-hook-form'
import { MuiFileInput } from 'mui-file-input'
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop'

import { FormHandleSubmit } from '../types'
import { Candidate } from '../../../types'

import 'react-image-crop/dist/ReactCrop.css'
import { FirebaseContext } from '../../../Contexts'

export const PhotoStep = ({
  id,
  data,
  setActiveForm,
}: {
  id: string
  data?: Candidate['photo']
  setActiveForm: (handleSubmit: () => FormHandleSubmit<any>) => void
}) => {
  const { storage, auth } = useContext(FirebaseContext)
  const [file, setFile] = useState<File | null>(null)
  const [imageURL, setImageURL] = useState<string | undefined>()
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>()
  const [completedCanvas, setCompletedCanvas] = useState<boolean>(false)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgCropRef = useRef<HTMLImageElement>(null)
  const handleChange = (newFile: typeof file) => {
    if (!newFile) {
      setFile(null)
      setImageURL('')
      setCompletedCanvas(false)
      setCompletedCrop(null)

      return
    }

    const reader = new FileReader()

    reader.readAsDataURL(newFile)
    reader.onload = function (e) {
      const img = document.createElement('img')
      const imgURL = e?.target?.result?.toString()

      if (!imgURL) return setFile(null)

      img.onload = () => {
        const MAX_WIDTH = 1200
        const MAX_HEIGHT = 1200
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        let width = img.width
        let height = img.height

        if (width <= MAX_WIDTH && height <= MAX_HEIGHT) return setImageURL(e?.target?.result?.toString())

        if (!ctx) return setFile(null)

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = height * (MAX_WIDTH / width)
            width = MAX_WIDTH
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = width * (MAX_HEIGHT / height)
            height = MAX_HEIGHT
          }
        }

        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0, width, height)

        setImageURL(canvas.toDataURL(newFile.type))
      }
      img.src = imgURL
    }

    setFile(newFile)
  }
  const canvasPreview = (
    image: HTMLImageElement,
    canvas: HTMLCanvasElement,
    crop: PixelCrop,
    scale = 1,
    rotate = 0,
  ) => {
    try {
      const ctx = canvas.getContext('2d')
      const scaleX = image.naturalWidth / image.width
      const scaleY = image.naturalHeight / image.height
      const pixelRatio = window.devicePixelRatio
      const cropX = crop.x * scaleX
      const cropY = crop.y * scaleY
      const rotateRads = (rotate * Math.PI) / 180
      const centerX = image.naturalWidth / 2
      const centerY = image.naturalHeight / 2

      if (!ctx) {
        throw new Error('No 2d context')
      }

      canvas.width = Math.floor(crop.width * scaleX * pixelRatio)
      canvas.height = Math.floor(crop.height * scaleY * pixelRatio)
      ctx.imageSmoothingQuality = 'high'

      ctx.scale(pixelRatio, pixelRatio)
      ctx.save()
      ctx.translate(-cropX, -cropY)
      ctx.translate(centerX, centerY)
      ctx.rotate(rotateRads)
      ctx.scale(scale, scale)
      ctx.translate(-centerX, -centerY)
      ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight)
      ctx.restore()
    } catch (error) {
      alert(error instanceof Error && error.message)
    }
  }
  const useDebounceEffect = (fn: () => void, waitTime: number, deps?: DependencyList) => {
    useEffect(() => {
      const t = setTimeout(() => {
        fn.apply(undefined, deps as any)
      }, waitTime)

      return () => {
        clearTimeout(t)
      }
    }, [deps, fn, waitTime])
  }

  useEffect(() => {
    const url: { url?: string } = { url: data?.url }
    const updateImage = async (): Promise<string | undefined> => {
      const uploadBlob = (blob: Blob | null): Promise<string | undefined> => {
        return new Promise((resolve, reject) => {
          if (!blob) return reject('Blob não encontrado')

          const filename = `photos/${auth.currentUser?.uid}.${blob.type.replace('image/', '')}`
          const storageRef = ref(storage, filename)

          uploadBytes(storageRef, blob).then((snapshop) => {
            getDownloadURL(storageRef).then((fileURL) => {
              url.url = fileURL
              resolve(fileURL)
            })
          })
        })
      }

      return new Promise((resolve, reject) => {
        if (!file) return resolve(data?.url)
        if (!completedCanvas) return uploadBlob(file).then(resolve).catch(reject)
        if (previewCanvasRef?.current)
          return previewCanvasRef?.current.toBlob((blob) => uploadBlob(blob).then(resolve).catch(reject))

        return resolve(data?.url)
      })
    }

    setActiveForm(() => ({
      formId: id,
      getData: () => url,
      submit: (onValid: (data: any) => void, onInvalid?: (errors: any) => void) => {
        return async () => {
          try {
            updateImage().then((url) =>
              url ? onValid({ url: url || '' }) : onInvalid?.([new Error('Image not uploaded')]),
            )
          } catch (error) {
            onInvalid?.([error])
          }
        }
      },
    }))
  }, [id, setActiveForm, data?.url, storage, auth, file, completedCanvas])

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgCropRef.current && previewCanvasRef.current) {
        canvasPreview(imgCropRef.current, previewCanvasRef.current, completedCrop)
        setCompletedCanvas(true)
      }
    },
    100,
    [completedCrop],
  )

  return (
    <Grid container item spacing={2} rowSpacing={2} style={{ marginTop: 10 }}>
      <Grid item xs={12} sm={6} md={6}>
        <MuiFileInput
          value={file}
          label="Seleccione una foto"
          onChange={handleChange}
          fullWidth
          getInputText={(value) => value?.name || ''}
          inputProps={{
            accept: 'image/*',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} style={{ display: completedCanvas ? 'block' : 'none' }}>
        <Grid item xs={12}>
          <canvas
            ref={previewCanvasRef}
            style={{
              border: '1px solid black',
              objectFit: 'contain',
              width: completedCrop?.width,
              height: completedCrop?.height,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setCompletedCrop(undefined)
              setCompletedCanvas(false)
            }}
          >
            ¿Recortar de nuevo?
          </Button>
        </Grid>
      </Grid>
      {file && !completedCanvas ? (
        <Grid item xs={12} sm={6} md={6}>
          <ReactCrop
            crop={crop}
            onChange={(crop) => setCrop(crop)}
            onComplete={(crop) => setCompletedCrop(crop)}
            style={{ borderRadius: 10 }}
          >
            <img src={imageURL} ref={imgCropRef} alt="" />
          </ReactCrop>
        </Grid>
      ) : data?.url && !completedCanvas ? (
        <Grid item xs={12} sm={6} md={6}>
          <img src={data?.url} alt="" referrer-policy="no-referrer" style={{ maxWidth: '100%' }} />
        </Grid>
      ) : null}
    </Grid>
  )
}
